import type { BdBackendHost } from './hosted-api-endpoint';

/**
 * Path roots of a Modulith host that need a token (BDA-6595).
 *
 * `msalConfig.protectedResources` is a positive list: what is in it gets a token or a login
 * redirect, everything else goes out anonymous. A hosted key whose value is the bare host base
 * (`<origin>/api`) would protect the whole host, public clinic pages included, so such a key is
 * replaced by one entry per root below (see `hostProtectedResources`).
 *
 * One entry is one first path segment under `/api`, with a leading slash: every root the host's
 * controllers map that is neither public (the `securityMatcher` roots of its scoped security
 * chains) nor a path variable. The backend test `ModulePathOwnershipTest` in
 * `backend/apps/meta-backend-api` computes that set from the code and pins it against a mirror of
 * this list, so a new root fails the backend build until both lists are updated together.
 *
 * A forgotten root fails safe: `bdOpportunisticAuthInterceptor` still attaches the token of a
 * signed-in user on every path of the host, an anonymous caller gets the backend's 401.
 */
export const META_BACKEND_PROTECTED_PATH_ROOTS: Readonly<Record<BdBackendHost, readonly string[]>> = {
  DE: [
    // ai
    '/klia-artifacts',
    '/klia-attachments',
    '/threads',
    // business-case
    '/business-case',
    // clinics-filters
    '/clinics-filters',
    // clinics (the other clinics roots are public; protected paths under them are keys of their own)
    '/kennzahlen-benchmarking',
    // de-dashboard
    '/dashboard',
    // demand
    '/clinic-accessibility-analysis',
    '/demand-analysis',
    '/leistungsgruppen',
    '/open-close-scenarios',
    '/sources',
    '/standorte',
    '/vorhalteverguetung',
    // drg-profit-loss-analysis
    '/drg-profit-loss',
    '/inek',
    // files
    '/files',
    // market
    '/markets',
    // potential
    '/potential-analysis',
    // scenario
    '/scenarios',
    // user-data-graph
    '/admin',
    '/companies',
    '/feature-permissions',
    '/folders',
    '/grant-requests',
    '/grants',
    '/groups',
    '/shareables',
    '/users',
    // virtual-clinic-api
    '/benchmarking-groups',
    '/case-export-templates',
    '/decision-cockpit',
    '/intuitive-fot',
    '/intuitive-surgical',
    '/operating-numbers',
    '/p21-imports',
    '/referral-imports',
    '/virtual-clinic-referral',
    '/virtual-clinics',
    // json-blob-store
    '/json-blobs',
    // task-status-module (job system)
    '/jobs',
  ],
  AT: [],
  CH: [],
};
